import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, contentChild, input, TemplateRef } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { PlatformService } from '@examdojo/platform';
import { ProfileAvatarComponent, UserQuery } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { SidenavItem } from './sidenav.model';

function getCompactViewFromDeepestRoute(router: Router): boolean {
  const shellRoute = router.routerState.root.firstChild;

  if (!shellRoute) {
    return false;
  }

  let current = shellRoute.snapshot.firstChild;
  let lastDefinedValue: boolean | undefined;

  while (current) {
    if (current.data?.['compactView'] !== undefined) {
      lastDefinedValue = current.data['compactView'];
    }
    current = current.firstChild;
  }

  return lastDefinedValue ?? false;
}

@Component({
  selector: 'dojo-sidenav',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TranslocoPipe,
    RouterLinkActive,
    MatSidenavModule,
    ProfileAvatarComponent,
    MatTooltip,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full flex flex-col' },
})
export class SidenavComponent {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly router: Router,
    private readonly colorSchemeService: ColorSchemeService,
    private readonly platformService: PlatformService,
  ) {}

  readonly items = input.required<SidenavItem[]>();
  readonly logoCompact = input.required<string>();
  readonly logoFull = input.required<string>();
  readonly logoFullDark = input.required<string>();
  readonly logoAlt = input<string>('Logo');
  readonly settingsRoute = input.required<string>();

  readonly customFooter = contentChild<TemplateRef<unknown>>('sidenavFooter');

  readonly isMobile$ = this.platformService.isSmall$.pipe(distinctUntilChanged());

  readonly isCompact$ = combineLatest([
    this.platformService.resize$.pipe(
      startWith(null),
      map(() => this.platformService.width < 1100),
      distinctUntilChanged(),
    ),
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => getCompactViewFromDeepestRoute(this.router)),
      startWith(false),
      distinctUntilChanged(),
    ),
  ]).pipe(
    map(([isScreenSmall, isCompactView]) => {
      return isScreenSmall || isCompactView;
    }),
  );

  readonly state = connectState({
    user: this.userQuery.active$,
    darkMode: this.colorSchemeService.hasDarkMode$,
    isMobile: this.isMobile$,
    isCompact: this.isCompact$,
  });

  navigateToProfile() {
    this.router.navigate([this.settingsRoute()]);
  }
}
